import styled from 'styled-components';
import { ChangeEvent, useState } from 'react';
import { LegTableView } from '@paradigm/features/src/unified-rfqs/ui/types';
import { blueMana, darkerBlackPearl } from '@paradigm/design-system/src/colors';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { isValidDecimal } from '@paradigm/utils/formatDecimal';
import { Divider as DefaultDivider } from '#/components/UnifiedRfqs/drfq/common';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import { ColoredAction } from '../../rfq-common/LegsTable';
import { LegQuantity } from './LegsTable';
import FormatterInput from './FormatterInput';

export type ChangeLegPriceFn = (args: {
  index: number;
  side: Side;
  value: string;
}) => void;

interface LegPriceView {
  readonly bid: string | null;
  readonly ask: string | null;
  readonly placeholder?: string;
  readonly precision?: number;
  readonly displayOnly?: boolean;
  readonly side: 'BUY' | 'SELL';
}

interface LegTableProps {
  readonly legs: readonly LegTableView[];
  readonly legPrices: readonly LegPriceView[];
  readonly changeLegPrice: ChangeLegPriceFn;
  readonly markPrices: string[];
  readonly legPricingEnabled: boolean;
  readonly isTwoWay?: boolean;
  readonly isLoading?: boolean;
  readonly showContractTerms?: boolean;
  readonly title?: string;
}

export default function LegsTable({
  legs,
  legPrices,
  changeLegPrice,
  markPrices,
  legPricingEnabled,
  isTwoWay = false,
  isLoading = false,
  showContractTerms = false,
  title,
}: LegTableProps) {
  if (isLoading) {
    return (
      <div css="text-align: center; padding: 1rem;">
        <LoadingSpinner alt="loading..." />
      </div>
    );
  }

  const hasTitle = title != null;

  return (
    <LegsContainer>
      {hasTitle && <Title>{title}</Title>}
      {legs.map((leg, index) => (
        <LegInfoCard
          key={leg.index}
          showContractTerms={showContractTerms}
          markPrice={markPrices[index] ?? NEGATIVE_SIGN}
          legPricingEnabled={legPricingEnabled}
          legPrice={legPrices[index]}
          isTwoWay={isTwoWay}
          changeLegPrice={changeLegPrice}
          leg={leg}
        />
      ))}
    </LegsContainer>
  );
}

interface LegInfoCard {
  readonly leg: LegTableView;
  readonly legPrice: LegPriceView | undefined;
  readonly markPrice?: string;
  readonly legPricingEnabled: boolean;
  readonly changeLegPrice?: (args: {
    index: number;
    side: Side;
    value: string;
  }) => void;
  readonly isTwoWay: boolean;
  readonly hideMarkPrice?: boolean;
  readonly showContractTerms?: boolean;
}

export function LegInfoCard({
  leg,
  legPrice,
  markPrice,
  legPricingEnabled,
  changeLegPrice,
  isTwoWay,
  hideMarkPrice = false,
  showContractTerms = false,
}: LegInfoCard) {
  return (
    <CardContainer id={`um-order-modal-leg-card-${leg.index}`}>
      <GridContainer>
        <Label>Product</Label>
        <Value>{leg.productLabel}</Value>
        <Label>Action</Label>
        <Value css="opacity: 1;">
          <ColoredAction>{leg.side}</ColoredAction>
        </Value>
        <Label>Instrument</Label>
        <Value>{leg.instrumentLabel}</Value>
        <Label>Expiry</Label>
        <Value>{leg.expiry}</Value>
        <Label>Strike</Label>
        <Value>{leg.strike}</Value>
        <Label disabled={!isValidDecimal(leg.price)}>Leg Price</Label>
        <Value>{leg.price}</Value>
        <Label>Ratio</Label>
        <Value>{leg.ratio}</Value>
        <Label>Qty</Label>
        <Value>
          <LegQuantity
            css={`
              && {
                text-align: end;
              }
            `}
            leg={leg}
            showContractTerms={showContractTerms}
          />
        </Value>
      </GridContainer>
      {!hideMarkPrice && (
        <>
          <Divider />
          <GridContainer>
            <Label>Mark</Label>
            <Value>{markPrice ?? NEGATIVE_SIGN}</Value>
          </GridContainer>
        </>
      )}
      {legPricingEnabled && legPrice != null && (
        <>
          <Divider />
          <GridContainer>
            <Label>Bid</Label>
            <Value css="opacity: 1;">
              <LegPriceInput
                fieldSide="BUY"
                leg={legPrice}
                index={leg.index}
                changeLegPrice={changeLegPrice}
                isTwoWay={isTwoWay}
              />
            </Value>
            <Label>Ask</Label>
            <Value css="opacity: 1;">
              <LegPriceInput
                fieldSide="SELL"
                leg={legPrice}
                index={leg.index}
                changeLegPrice={changeLegPrice}
                isTwoWay={isTwoWay}
              />
            </Value>
          </GridContainer>
        </>
      )}
    </CardContainer>
  );
}

interface LegPriceInputProps {
  fieldSide: Side;
  index: number;
  leg: LegPriceView;
  isTwoWay: boolean;
  changeLegPrice?: ChangeLegPriceFn;
}

function LegPriceInput({
  fieldSide,
  index,
  leg,
  isTwoWay,
  changeLegPrice,
}: LegPriceInputProps) {
  const [hasFocus, setHasFocus] = useState(false);
  const value = fieldSide === 'BUY' ? leg.bid : leg.ask;
  const isDisabled = leg.side !== fieldSide && !isTwoWay;

  const onChange = (event: ChangeEvent<HTMLInputElement>) =>
    changeLegPrice?.({ index, side: fieldSide, value: event.target.value });

  if (leg.displayOnly === true) {
    return <span>{value ?? NEGATIVE_SIGN}</span>;
  }

  if (!hasFocus && !isDisabled && value === '') {
    return <EnterButton onClick={() => setHasFocus(true)} />;
  }

  return (
    <FormattedInput
      onBlur={() => setHasFocus(false)}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={hasFocus}
      aria-label={`leg ${index + 1} ${fieldSide} price`}
      disabled={isDisabled}
      thousandSeparator
      decimalScale={leg.precision}
      value={value}
      allowNegative={false}
      placeholder={leg.placeholder}
      onChange={onChange}
    />
  );
}

const EnterButton = styled.button.attrs({
  children: 'Enter',
})`
  border: 0;
  padding: 0;
  width: 80px;
  height: 28px;
  display: flex;
  justify-content: end;
  align-items: center;
  color: ${blueMana};
  font-size: 1rem;
  background-color: transparent;
`;

export const LegsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
`;

const CardContainer = styled.div`
  background-color: ${darkerBlackPearl};
  padding: 0.75rem 1.1rem 1rem 1rem;
  border-radius: 3px;
  width: 100%;
  font-size: 1rem;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
`;

const Label = styled.label<{ disabled?: boolean }>`
  color: ${blueMana};
  margin: 0;

  ${({ disabled = false }) => disabled && 'opacity: 0.5;'}
`;

const Value = styled.div`
  color: white;
  opacity: 0.7;
  font-weight: 400;
  justify-self: end;
`;

export const Divider = styled(DefaultDivider)`
  background-color: ${darkerBlackPearl};
  margin: 1rem -1rem;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: normal;
  margin-bottom: -0.5rem;
`;

const FormattedInput = styled(FormatterInput)`
  max-width: 80px;
  margin: 0;
  text-align: end;
`;
